import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import FilterDropdown from '../../FilterDropdown/FilterDropdown';
import FilterButton from '../../FilterButton/FilterButton';
import useFilterState from '../../../hooks/useFilterState';
import { filtersData } from '../../../constants/filtersData';
import { filterNames } from '../../../constants/filterNames';
import FilterContainer from '../../FilterContainer/FilterContainer';
import SelectedOption from '../../SelectedOption/SelectedOption';
import { markMapKeys, markNames, marksMap } from '../../../constants/marks';
import { dspxDiffDays } from '../../../../../helpers/dspx-utils';
import useHiddenMarksItem from '../../../hooks/useHiddenMarksItem';
import { useFilterOptionsShorter } from '../../../hooks/useFilterOptionsShorter';
import { HiddenSelectedOptions } from '../../HiddenSelectedOptions/HiddenSelectedOptions';

const filter = filtersData[filterNames.marks];

const increaseMarkAmount = (name, filterMap) => {
  const key = markMapKeys[name];

  const mark = filterMap.get(key);

  filterMap.set(key, { ...mark, amount: mark.amount + 1 });
};

const MarksFilter = ({ disabled = false }) => {
  const [items, setItems] = useState([]);
  const [filterState, setFilterState] = useFilterState(filter);

  const filteredRequests = useSelector(
    (state) => state.statisticsStore.filteredRequests
  );

  const handleItemRemove = (id) => {
    const newState = filterState.filter((itemId) => itemId !== id);

    setFilterState(newState);
  };

  const hiddenMarksItem = useHiddenMarksItem();

  const hasFilter = filterState.length > 0 || hiddenMarksItem;

  const allSelected = hasFilter && marksMap.size === filterState.length;

  useEffect(() => {
    if (filteredRequests?.length > 0) {
      const filterMapClone = new Map(marksMap);

      filteredRequests.forEach((request) => {
        const {
          hasRecord,
          hasAttachment,
          isInternal,
          isImmediate,
          isChargeable,
          possWarranty,
          warranty,
          isRetry,
          expired,
          noWarranty,
        } = markNames;

        if (request[hasRecord]) {
          increaseMarkAmount(hasRecord, filterMapClone);
        }

        if (request[hasAttachment]) {
          increaseMarkAmount(hasAttachment, filterMapClone);
        }

        if (request[isInternal]) {
          increaseMarkAmount(isInternal, filterMapClone);
        }

        if (request[isImmediate]) {
          increaseMarkAmount(isImmediate, filterMapClone);
        }

        if (request[isChargeable]) {
          increaseMarkAmount(isChargeable, filterMapClone);
        }

        if (request[isRetry]) {
          increaseMarkAmount(isRetry, filterMapClone);
        }

        if (request.garantyId === 1) {
          increaseMarkAmount(warranty, filterMapClone);
        }

        if (request.garantyId === 2) {
          increaseMarkAmount(possWarranty, filterMapClone);
        }

        if (!request.garanty || request.garantyId === 0) {
          increaseMarkAmount(noWarranty, filterMapClone);
        }

        const diff = request.doneDate
          ? dspxDiffDays(request.doneDate, request.executeTime)
          : dspxDiffDays(0, request.executeTime);

        if (diff > 0) {
          increaseMarkAmount(expired, filterMapClone);
        }
      });

      const items = Array.from(filterMapClone.values())
        .filter((item) => item.amount !== 0 && item.id !== hiddenMarksItem)
        .sort((a, b) => b.amount - a.amount);

      setItems(items);
    }
  }, [filteredRequests]);

  const filteredItems = items.filter((item) => filterState.includes(item.id));
  const { visibleItems, hiddenItems } = useFilterOptionsShorter(filteredItems);

  return (
    <FilterContainer>
      <FilterDropdown
        disabled={disabled}
        filter={filter}
        items={items}
        getItemContent={(item) => (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {item.icon} {item.description}
          </span>
        )}
        getSearchContent={(item) => item.description}
        renderButton={({ visible }) => (
          <FilterButton
            text="Признак"
            opened={visible}
            selected={hasFilter}
          />
        )}
      />
      {hiddenMarksItem && (
        <SelectedOption text={marksMap.get(hiddenMarksItem).description} />
      )}
      {allSelected ? (
        <SelectedOption
          text="Все"
          onRemove={() => setFilterState([])}
        />
      ) : (
        <>
          {visibleItems.map((item) => (
            <SelectedOption
              key={item.id}
              text={item.description}
              value={item.id}
              onRemove={handleItemRemove}
            />
          ))}
          {Array.isArray(hiddenItems) && !!hiddenItems.length && (
            <HiddenSelectedOptions
              hiddenItems={hiddenItems}
              handleItemRemove={handleItemRemove}
            />
          )}
        </>
      )}
    </FilterContainer>
  );
};

export default MarksFilter;
